import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import UserProfileModal from '../../../components/common/UserProfileModal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { cloneEvent, closeEvent, deleteEvent, EVENTS_CHANGED_EVENT, fetchEvent, fetchMyEvents, hideEvent, reopenEvent, respondToEvent, unhideEvent } from '../services/eventService';
import CreateEventWizard from './CreateEventWizard';
import EventDetailModal from './EventDetailModal';
import RsvpDetailsModal from './RsvpDetailsModal';
import { needsRsvpDetailsOnAccept, hasEnabledRsvpFields } from '../constants/rsvpFields';
import { isExpenseSharingEvent } from '../constants/expenseFields';
import { findMyShareBalance, formatShareMoney } from '../utils/expenseShareUtils';
import { isPollEvent, selectedPollOptions, followUpParticipantsFromPoll, pollDisplayName, resolvePollConfig, tallyPollVotes } from '../utils/pollUtils';
import FollowUpEventPrompt from './FollowUpEventPrompt';
import SharePersonalAmount from './SharePersonalAmount';
import { EVENT_CATEGORIES } from '../constants/eventTypes';
import {
  eventDtoToTimelineInfo,
  formatRsvpSummary,
  getStatusLabel,
  getTypeMeta,
  PARTICIPANT_STATUS_BADGE,
  PARTICIPANT_STATUS_LABEL,
} from '../utils/eventTimelineUtils';

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const COMPLETED_STATUSES = new Set(['COMPLETED', 'POST_COMPLETION', 'CLOSED']);

const formatClock = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${meridiem}`;
};

const ROW_PALETTES = [
  { row: 'bg-emerald-50 border-emerald-100 hover:border-emerald-200', date: 'text-emerald-600', icon: 'bg-emerald-100' },
  { row: 'bg-sky-50 border-sky-100 hover:border-sky-200', date: 'text-sky-600', icon: 'bg-sky-100' },
  { row: 'bg-pink-50 border-pink-100 hover:border-pink-200', date: 'text-pink-600', icon: 'bg-pink-100' },
  { row: 'bg-amber-50 border-amber-100 hover:border-amber-200', date: 'text-amber-600', icon: 'bg-amber-100' },
  { row: 'bg-violet-50 border-violet-100 hover:border-violet-200', date: 'text-violet-600', icon: 'bg-violet-100' },
];

const paletteFor = (event) => {
  const type = String(event?.eventType || '').toUpperCase();
  if (type === 'ACTION_TRACKING') return ROW_PALETTES[0];
  if (type === 'SPLITWISE') return ROW_PALETTES[4];
  if (type === 'EXPENSE_TRACKING') return ROW_PALETTES[3];
  if (type === 'ATTENDANCE') return ROW_PALETTES[3];
  if (type === 'MEETING') return ROW_PALETTES[1];
  const seed = String(event?.id || event?.name || 'x').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return ROW_PALETTES[seed % ROW_PALETTES.length];
};

const eventDateParts = (event) => {
  const startDate = event?.schedule?.startDate;
  if (!startDate) return null;
  const parts = String(startDate).slice(0, 10).split('-').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  return {
    day: String(day),
    month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
    weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase(),
  };
};

const eventTimeRange = (event) => {
  const schedule = event?.schedule || {};
  if (!schedule.startDate) return 'No date set';
  if (schedule.allDay) return 'All day';
  const start = formatClock(schedule.startTime);
  const end = formatClock(schedule.endTime);
  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  return 'Time TBD';
};

const startOfLocalDay = (year, month, day) => new Date(year, month - 1, day).getTime();

const parseScheduleDay = (raw) => {
  if (!raw) return null;
  const parts = String(raw).slice(0, 10).split('-').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return startOfLocalDay(parts[0], parts[1], parts[2]);
};

const eventRelativeLabel = (event) => {
  const schedule = event?.schedule || {};
  const startMs = parseScheduleDay(schedule.startDate);
  if (startMs == null) return null;
  const now = new Date();
  const todayMs = startOfLocalDay(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const endMs = parseScheduleDay(schedule.endDate) || startMs;
  const dayMs = 24 * 60 * 60 * 1000;
  if (todayMs >= startMs && todayMs <= endMs) return { text: 'Today', tone: 'text-emerald-600' };
  if (startMs > todayMs) {
    const days = Math.round((startMs - todayMs) / dayMs);
    if (days === 1) return { text: 'Tomorrow · 1 day left', tone: 'text-sky-600' };
    return { text: `${days} days left`, tone: 'text-sky-600' };
  }
  const days = Math.round((todayMs - endMs) / dayMs);
  if (days === 1) return { text: 'Yesterday · 1 day ago', tone: 'text-gray-500' };
  return { text: `${days} days ago`, tone: 'text-gray-500' };
};

const categoryLabel = (id) => EVENT_CATEGORIES.find((item) => item.id === id)?.label || '';

const eventChips = (event) => {
  const chips = [];
  const category = categoryLabel(event?.category);
  if (category) chips.push(category);
  (event?.tags || []).forEach((tag) => {
    if (tag && !chips.some((item) => item.toLowerCase() === String(tag).toLowerCase())) chips.push(tag);
  });
  return chips.slice(0, 3);
};

const ClockIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
  </svg>
);

const organizerInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const startOfWeekIso = () => {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
};

const endOfWeekIso = () => {
  const now = new Date();
  const day = now.getDay();
  const sundayOffset = day === 0 ? 0 : 7 - day;
  const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + sundayOffset);
  return `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
};

const eventStartIso = (event) => String(event?.schedule?.startDate || '').slice(0, 10);

const matchesDateFilter = (event, dateFilter) => {
  if (dateFilter === 'all') return true;
  const start = eventStartIso(event);
  if (!start) return dateFilter === 'upcoming';
  if (dateFilter === 'upcoming') return start >= todayIso();
  if (dateFilter === 'today') {
    const today = todayIso();
    const end = String(event?.schedule?.endDate || start).slice(0, 10) || start;
    return start <= today && end >= today;
  }
  if (dateFilter === 'week') return start >= startOfWeekIso() && start <= endOfWeekIso();
  if (dateFilter === 'month') {
    const now = new Date();
    const prefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return start.startsWith(prefix);
  }
  return true;
};

const matchesSearch = (event, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [
    event?.name,
    event?.creatorName,
    event?.category,
    event?.eventType,
    ...(event?.tags || []),
  ].join(' ').toLowerCase();
  return hay.includes(q);
};

const isCompletedEvent = (event) => {
  const status = String(event?.status || '').toUpperCase();
  const eventType = String(event?.eventType || '').toUpperCase();
  if (eventType === 'POLL' || eventType === 'ACTION_TRACKING') {
    return status === 'CANCELLED';
  }
  if (status === 'DRAFT' || status === 'CANCELLED' || status === 'DELETED') return false;
  if (COMPLETED_STATUSES.has(status) || status === 'EXPIRED') return true;
  return isPastExpiry(event);
};

const isPastExpiry = (event) => {
  const schedule = event?.schedule || {};
  const rawDate = schedule.expiresAt || schedule.endDate || schedule.startDate;
  if (!rawDate) return false;
  const datePart = String(rawDate).slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return false;
  let timePart = schedule.expiresAtTime || schedule.endTime || schedule.startTime || '';
  if (String(rawDate).length >= 16 && String(rawDate).charAt(10) === 'T' && !schedule.expiresAtTime) {
    timePart = String(rawDate).slice(11, 16);
  }
  if (!timePart || (schedule.allDay && !schedule.expiresAtTime)) timePart = '23:59';
  const match = String(timePart).match(/^(\d{1,2}):(\d{2})/);
  const hours = match ? parseInt(match[1], 10) : 23;
  const minutes = match ? parseInt(match[2], 10) : 59;
  const expiry = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return expiry.getTime() <= Date.now();
};

const eventCreatedKey = (event) => String(event?.createdAt || event?.updatedAt || '');

const splitAndSort = (list) => {
  const active = [];
  const completed = [];
  list.forEach((event) => {
    if (isCompletedEvent(event)) completed.push(event);
    else active.push(event);
  });
  const newestFirst = (a, b) => eventCreatedKey(b).localeCompare(eventCreatedKey(a));
  active.sort(newestFirst);
  completed.sort(newestFirst);
  return { active, completed };
};

const countLabel = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const RESPONDED_STATUSES = new Set(['ACCEPTED', 'DECLINED', 'TENTATIVE']);

const eventListSummary = (event) => {
  const participants = event?.participants || [];
  if (isPollEvent(event)) {
    const { options } = resolvePollConfig(event);
    const { voterCount } = tallyPollVotes(options, participants, event);
    return `${countLabel(voterCount, 'response', 'responses')} received`;
  }
  const responseType = String(event?.responsePolicy?.type || '').toUpperCase();
  const eventType = String(event?.eventType || '').toUpperCase();
  if (
    ['RSVP', 'ACCEPT_REJECT', 'YES_NO_MAYBE'].includes(responseType)
    || ['MEETING', 'ATTENDANCE', 'TASK'].includes(eventType)
  ) {
    const invitees = participants.filter((p) => !sameId(p.userId, event.creatorId));
    const received = invitees.filter((p) => RESPONDED_STATUSES.has(String(p.status || '').toUpperCase())).length;
    return `${countLabel(received, 'response', 'responses')} received`;
  }
  if (isExpenseSharingEvent(event?.eventType)) {
    const entries = Number(event?.expenseSummary?.entryCount) || 0;
    if (entries > 0) return countLabel(entries, 'expense', 'expenses');
  }
  return null;
};

const PAGE_SIZE = 10;

const getResponseOptions = (responseType) => {
  const type = (responseType || 'NONE').toUpperCase();
  if (type === 'NONE') return [];
  if (type === 'RSVP') {
    return [
      { id: 'ACCEPTED', label: 'Accept invitation', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
      { id: 'TENTATIVE', label: 'Maybe', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
      { id: 'DECLINED', label: 'Decline', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
    ];
  }
  if (type === 'YES_NO_MAYBE') {
    return [
      { id: 'ACCEPTED', label: 'Yes', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
      { id: 'TENTATIVE', label: 'Maybe', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
      { id: 'DECLINED', label: 'No', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
    ];
  }
  return [
    { id: 'ACCEPTED', label: 'Accept', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
    { id: 'DECLINED', label: 'Decline', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
  ];
};

const EventRow = ({ event, onOpen, onEdit, onDelete, onClone, onOpenOrganizer, onToggleHidden, hiddenMode, currentUserId, directoryUsers = [] }) => {
  const typeMeta = getTypeMeta(event.eventType);
  const viewerStatus = (event.viewerStatus || '').toUpperCase();
  const isPoll = isPollEvent(event);
  const mine = (event.participants || []).find((p) => sameId(p.userId, currentUserId));
  const hasVoted = isPoll && (
    viewerStatus === 'ACCEPTED'
    || selectedPollOptions(mine, event).length > 0
  );
  const statusBadge = PARTICIPANT_STATUS_BADGE[viewerStatus] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  const tagLabel = viewerStatus
    ? (PARTICIPANT_STATUS_LABEL[viewerStatus] || viewerStatus)
    : getStatusLabel(event.status);
  const hideStatus = isPoll && (
    String(tagLabel || '').toLowerCase() === 'waiting for response'
    || String(event.status || '').toUpperCase() === 'WAITING_FOR_RESPONSE'
  );
  const showShareAmount = isExpenseSharingEvent(event.eventType);
  const palette = paletteFor(event);
  const dateParts = eventDateParts(event);
  const directoryUser = (directoryUsers || []).find((user) => sameId(user.id, event.creatorId));
  const creatorParticipant = (event.participants || []).find((p) => sameId(p.userId, event.creatorId));
  const organizer = event.creatorName
    || `${directoryUser?.firstName || ''} ${directoryUser?.lastName || ''}`.trim()
    || directoryUser?.username
    || creatorParticipant?.userName
    || (event.viewerIsCreator ? 'You' : 'Organizer');
  const organizerPhoto = directoryUser?.profilePicture || creatorParticipant?.profilePicture || null;
  const relative = eventRelativeLabel(event);
  const chips = eventChips(event);
  const summary = eventListSummary(event);
  const canEdit = onEdit && event.viewerIsCreator && !['CANCELLED', 'EXPIRED', 'CLOSED'].includes(String(event.status || '').toUpperCase());
  const canDelete = onDelete && event.viewerIsCreator;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(event);
        }
      }}
      className={`w-full text-left rounded-2xl border px-3 sm:px-4 py-3 hover:shadow-sm transition-colors cursor-pointer ${palette.row} ${
        isPoll && hasVoted ? 'ring-1 ring-emerald-200 dark:ring-emerald-800' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {dateParts && (
          <div className={`flex-shrink-0 w-[4.5rem] h-[4.5rem] rounded-2xl bg-white/90 dark:bg-gray-900/50 flex flex-col items-center justify-center leading-none ${palette.date}`}>
            <span className="text-2xl font-black">{dateParts.day}</span>
            <span className="text-[11px] font-bold tracking-wider mt-0.5">{dateParts.month}</span>
            <span className="text-[11px] font-semibold tracking-wide opacity-80">{dateParts.weekday}</span>
          </div>
        )}
        <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-xl ${palette.icon}`}>
          <span aria-hidden="true">{typeMeta.icon}</span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-900 truncate">
            {event.name || 'Untitled event'}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            {dateParts && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon />
                {eventTimeRange(event)}
              </span>
            )}
            {relative && (
              <span className={`inline-flex items-center font-semibold ${relative.tone}`}>
                {relative.text}
              </span>
            )}
            {summary && (
              <span className="inline-flex items-center font-semibold text-gray-600">
                {summary}
              </span>
            )}
          </div>
          {chips.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/80 text-gray-600 border border-white/60"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>
        {showShareAmount && (
          <SharePersonalAmount
            balances={event.expenseSummary?.balances}
            userId={currentUserId}
            currency={event.configuration?.currency || 'INR'}
            size="md"
            align="right"
          />
        )}
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
          {event.creatorId ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenOrganizer?.(event.creatorId);
              }}
              className="inline-flex items-center gap-1.5 rounded-full pl-0.5 pr-2 py-0.5 hover:bg-white/70"
              title={`View ${organizer}`}
            >
              <PresignedAvatar
                fileKey={organizerPhoto}
                cacheKey={directoryUser?.username || event.creatorId}
                name={organizer}
                initials={organizerInitials(organizer)}
                className="w-6 h-6 rounded-full object-cover"
                fallbackClassName="w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br from-sky-400 to-violet-500 text-white text-[10px] font-bold"
              />
              <span className="truncate max-w-[8rem] text-xs font-semibold text-sky-700 hover:underline">{organizer}</span>
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600">
                {organizerInitials(organizer)}
              </span>
              <span className="truncate max-w-[8rem] text-xs">{organizer}</span>
            </span>
          )}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              {canEdit && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(event);
                  }}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  title="Edit"
                  aria-label="Edit"
                >
                  <PencilIcon />
                </button>
              )}
              {onClone && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClone(event);
                  }}
                  className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50"
                >
                  Clone
                </button>
              )}
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/80 text-indigo-600 border border-indigo-100">
                {typeMeta.label}
              </span>
              {!hideStatus && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${statusBadge}`}>
                  {viewerStatus === 'ACCEPTED' ? '✓ ' : ''}
                  {tagLabel}
                </span>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(event);
                  }}
                  className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                >
                  Delete
                </button>
              )}
            </div>
            {onToggleHidden && (
              <button
                type="button"
                title={hiddenMode ? 'Show this event again' : "Don't show me"}
                aria-label={hiddenMode ? 'Show this event again' : "Don't show me"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHidden(event, !hiddenMode);
                }}
                className="p-2 rounded-full text-gray-400 hover:text-gray-800 hover:bg-white/80"
              >
                {hiddenMode ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EventList = ({ events, onOpen, onEdit, onDelete, onClone, onOpenOrganizer, onToggleHidden, hiddenMode, emptyLabel, currentUserId, directoryUsers }) => {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [events]);

  if (!events.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-6 text-center">
        {emptyLabel}
      </p>
    );
  }

  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * PAGE_SIZE;
  const visible = events.slice(start, start + PAGE_SIZE);
  const from = start + 1;
  const to = start + visible.length;

  return (
    <div className="space-y-2.5">
      {visible.map((event) => (
        <EventRow
          key={event.id}
          event={event}
          onOpen={onOpen}
          onEdit={onEdit}
          onDelete={onDelete}
          onClone={onClone}
          onOpenOrganizer={onOpenOrganizer}
          onToggleHidden={onToggleHidden}
          hiddenMode={hiddenMode}
          currentUserId={currentUserId}
          directoryUsers={directoryUsers}
        />
      ))}
      {events.length > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {from}–{to} of {events.length}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 px-1">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EventsHomePanel = ({ onClose, openEventId }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const directoryUsers = useSelector((state) => state.users?.users || []);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [respondError, setRespondError] = useState('');
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null);
  const [rsvpFieldsForModal, setRsvpFieldsForModal] = useState(null);
  const [editingRsvp, setEditingRsvp] = useState(false);
  const [tab, setTab] = useState('mine');
  const [typeFilter, setTypeFilter] = useState('all');
  const [mineStatus, setMineStatus] = useState('active');
  const [showWizard, setShowWizard] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [followUpSource, setFollowUpSource] = useState(null);
  const [followUpType, setFollowUpType] = useState(null);
  const [followUpPeople, setFollowUpPeople] = useState(null);
  const [followUpChannelId, setFollowUpChannelId] = useState(null);
  const [followUpPollName, setFollowUpPollName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [profileUserId, setProfileUserId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMyEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    dispatch(fetchUsers());
  }, [load, dispatch]);

  useEffect(() => {
    const onChanged = (event) => {
      const dto = event?.detail?.event;
      if (dto?.id) {
        setEvents((prev) => {
          const exists = prev.some((item) => sameId(item.id, dto.id));
          return exists
            ? prev.map((item) => (sameId(item.id, dto.id) ? { ...item, ...dto } : item))
            : [dto, ...prev];
        });
        const status = String(dto.viewerStatus || '').toUpperCase();
        if (status === 'ACCEPTED') {
          setTab('mine');
          setMineStatus('active');
        }
      }
      load();
    };
    window.addEventListener(EVENTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(EVENTS_CHANGED_EVENT, onChanged);
  }, [load]);

  useEffect(() => {
    if (!openEventId) return undefined;
    let cancelled = false;
    const openTarget = async () => {
      try {
        const dto = await fetchEvent(openEventId);
        if (cancelled || !dto?.id) return;
        setEvents((prev) => (prev.some((item) => sameId(item.id, dto.id))
          ? prev.map((item) => (sameId(item.id, dto.id) ? { ...item, ...dto } : item))
          : [dto, ...prev]));
        setSelected(dto);
        if (dto.viewerHidden) {
          setTab('hidden');
        } else if (isActiveForUser(dto)) {
          setTab('mine');
          setMineStatus(isCompletedEvent(dto) ? 'completed' : 'active');
        } else {
          setTab('invited');
        }
      } catch {
        // Event may no longer be visible to this user
      }
    };
    openTarget();
    return () => {
      cancelled = true;
    };
  }, [openEventId]);

  const visibleEvents = useMemo(
    () => events.filter((event) => !event?.viewerHidden && !isDraftEvent(event) && !isDeletedEvent(event)),
    [events],
  );
  const mine = useMemo(() => {
    const base = visibleEvents.filter(isActiveForUser);
    if (typeFilter === 'attendance' || typeFilter === 'invitation') {
      events.forEach((event) => {
        if (!event?.viewerHidden || isDraftEvent(event) || isDeletedEvent(event)) return;
        if (!matchesTypeFilter(event, typeFilter) || !isActiveForUser(event)) return;
        if (!base.some((item) => sameId(item.id, event.id))) base.push(event);
      });
    }
    return splitAndSort(base);
  }, [events, visibleEvents, typeFilter]);
  const invited = useMemo(
    () => splitAndSort(visibleEvents.filter((event) => !isActiveForUser(event) && !isDeclinedForUser(event))),
    [visibleEvents],
  );
  const draftsList = useMemo(
    () => events
      .filter(isDraftEvent)
      .filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter))
      .sort((a, b) => eventCreatedKey(b).localeCompare(eventCreatedKey(a))),
    [events, searchQuery, dateFilter, typeFilter],
  );
  const deletedList = useMemo(
    () => events
      .filter(isDeletedEvent)
      .filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter))
      .sort((a, b) => eventCreatedKey(b).localeCompare(eventCreatedKey(a))),
    [events, searchQuery, dateFilter, typeFilter],
  );
  const hiddenList = useMemo(
    () => events
      .filter((event) => event?.viewerHidden && !isDraftEvent(event) && !isDeletedEvent(event))
      .filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter))
      .sort((a, b) => eventCreatedKey(b).localeCompare(eventCreatedKey(a))),
    [events, searchQuery, dateFilter, typeFilter],
  );
  const mineList = useMemo(() => {
    const source = mineStatus === 'completed' ? mine.completed : mine.active;
    return source.filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter));
  }, [mine, mineStatus, searchQuery, dateFilter, typeFilter]);
  const invitedActive = useMemo(
    () => invited.active.filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter)),
    [invited.active, searchQuery, dateFilter, typeFilter],
  );
  const invitedCompleted = useMemo(
    () => invited.completed.filter((event) => matchesSearch(event, searchQuery) && matchesDateFilter(event, dateFilter) && matchesTypeFilter(event, typeFilter)),
    [invited.completed, searchQuery, dateFilter, typeFilter],
  );

  const selectedInfo = selected ? eventDtoToTimelineInfo(selected) : null;
  const myParticipant = useMemo(() => {
    const list = selected?.participants || [];
    return list.find((p) => sameId(p.userId, currentUserId));
  }, [selected, currentUserId]);
  const myStatus = (myParticipant?.status || selected?.viewerStatus || 'PENDING').toUpperCase();
  const hasDecided = myStatus !== 'PENDING' && myStatus !== 'INVITED';
  const responseType = selectedInfo?.responseType || selected?.responsePolicy?.type || 'NONE';
  const isPoll = isPollEvent(selectedInfo || selected);
  const canRespond = Boolean(selectedInfo?.eventId)
    && selectedInfo?.status !== 'CANCELLED'
    && selectedInfo?.status !== 'DELETED'
    && selectedInfo?.status !== 'EXPIRED'
    && selectedInfo?.status !== 'CLOSED'
    && String(responseType).toUpperCase() !== 'NONE'
    && (isPoll || !sameId(selected?.creatorId, currentUserId));

  const applyUpdated = (dto) => {
    if (!dto?.id) return;
    setEvents((prev) => prev.map((item) => (item.id === dto.id ? {
      ...item,
      ...dto,
      viewerIsCreator: dto.viewerIsCreator ?? item.viewerIsCreator,
      viewerStatus: myStatusFrom(dto, currentUserId) || dto.viewerStatus || item.viewerStatus,
      viewerHidden: dto.viewerHidden ?? item.viewerHidden,
    } : item)));
    setSelected((prev) => (prev?.id === dto.id ? {
      ...prev,
      ...dto,
      viewerIsCreator: dto.viewerIsCreator ?? prev.viewerIsCreator,
      viewerStatus: myStatusFrom(dto, currentUserId) || dto.viewerStatus || prev.viewerStatus,
      viewerHidden: dto.viewerHidden ?? prev.viewerHidden,
    } : prev));
  };

  const handleCloseEvent = async () => {
    if (!selected?.id || saving) return;
    setSaving(true);
    setRespondError('');
    try {
      const updated = await closeEvent(selected.id);
      applyUpdated(updated);
      if (isPollEvent(updated || selected)) {
        setSelected(null);
        setFollowUpSource(updated || selected);
      }
    } catch (err) {
      setRespondError(err?.response?.data?.error || err?.message || 'Failed to close event');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenEvent = async () => {
    if (!selected?.id || saving) return;
    setSaving(true);
    setRespondError('');
    try {
      const updated = await reopenEvent(selected.id);
      applyUpdated(updated);
    } catch (err) {
      setRespondError(err?.response?.data?.error || err?.message || 'Failed to reopen poll');
    } finally {
      setSaving(false);
    }
  };

  const handleRespond = async (response, details) => {
    if (!selected?.id || saving) return;
    setSaving(true);
    setRespondError('');
    try {
      const updated = await respondToEvent(selected.id, {
        response,
        rsvpDetails: details || undefined,
      });
      applyUpdated(updated);
      if (String(response).toUpperCase() === 'ACCEPTED') {
        setTab('mine');
        setMineStatus('active');
      }
      setShowRsvpModal(false);
      setPendingResponse(null);
      setRsvpFieldsForModal(null);
      setEditingRsvp(false);
    } catch (err) {
      setRespondError(err?.response?.data?.error || err?.message || 'Failed to submit response');
    } finally {
      setSaving(false);
    }
  };

  const handleRespondRequest = async (response) => {
    if (response !== 'ACCEPTED') {
      handleRespond(response);
      return;
    }
    let type = responseType;
    let fields = selectedInfo?.rsvpFields;
    try {
      const fresh = await fetchEvent(selected.id);
      type = String(fresh?.responsePolicy?.type || type || '').toUpperCase();
      fields = fresh?.responsePolicy?.rsvpFields ?? fields;
    } catch {
      // use cached fields
    }
    if (needsRsvpDetailsOnAccept(type, fields)) {
      setRsvpFieldsForModal(fields);
      setPendingResponse(response);
      setEditingRsvp(false);
      setShowRsvpModal(true);
      return;
    }
    handleRespond(response);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.id || deleting) return;
    setDeleting(true);
    setError('');
    try {
      const updated = await deleteEvent(pendingDelete.id);
      if (updated?.id) {
        applyUpdated({ ...updated, status: updated.status || 'DELETED' });
        setTab('deleted');
      } else {
        setEvents((prev) => prev.filter((item) => item.id !== pendingDelete.id));
      }
      if (selected?.id === pendingDelete.id) setSelected(null);
      setPendingDelete(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleClone = async (event) => {
    const eventId = event?.id;
    if (!eventId) return;
    setError('');
    try {
      const cloned = await cloneEvent(eventId);
      if (!cloned?.id) throw new Error('Failed to clone event');
      setEvents((prev) => [cloned, ...prev.filter((item) => item.id !== cloned.id)]);
      setSelected(null);
      setTab('drafts');
      setEditingEvent(cloned);
      setShowWizard(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to clone event');
    }
  };

  const handleToggleHidden = async (event, hide) => {
    if (!event?.id) return;
    setError('');
    try {
      const updated = hide ? await hideEvent(event.id) : await unhideEvent(event.id);
      applyUpdated({ ...updated, viewerHidden: hide });
      if (hide && selected?.id === event.id) setSelected(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || (hide ? 'Failed to hide event' : 'Failed to show event'));
    }
  };

  const canEditEvent = (event) => Boolean(event?.viewerIsCreator)
    && !['CANCELLED', 'DELETED', 'EXPIRED', 'CLOSED'].includes(String(event?.status || '').toUpperCase());

  const openNewWizard = () => {
    setEditingEvent(null);
    setSelected(null);
    setShowWizard(true);
  };

  const openEditWizard = async (event) => {
    if (!event?.id) return;
    setSelected(null);
    setRespondError('');
    try {
      const fresh = await fetchEvent(event.id);
      setEditingEvent(fresh);
      setShowWizard(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open event');
    }
  };

  const handleEditRsvp = () => {
    setRsvpFieldsForModal(selectedInfo?.rsvpFields);
    setPendingResponse('ACCEPTED');
    setEditingRsvp(true);
    setShowRsvpModal(true);
  };

  const shareTotals = useMemo(() => {
    let owed = 0;
    let owe = 0;
    let currency = 'INR';
    events.forEach((event) => {
      if (event?.viewerHidden) return;
      if (!isExpenseSharingEvent(event?.eventType)) return;
      if (event.configuration?.currency) currency = event.configuration.currency;
      const net = Number(findMyShareBalance(event.expenseSummary?.balances || [], currentUserId)?.net) || 0;
      if (net > 0.004) owed += net;
      else if (net < -0.004) owe += Math.abs(net);
    });
    return { owed, owe, currency };
  }, [events, currentUserId]);

  const statusCounts = tab === 'mine'
    ? { active: mine.active.length, completed: mine.completed.length }
    : { active: invited.active.length, completed: invited.completed.length };

  const filterChip = (selectedChip, onClick, label, count, extra = '', compact = false) => (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded-full border font-semibold transition-colors ${
        compact ? 'gap-1 px-2 py-0.5 text-[10px]' : 'gap-1.5 px-3 py-1.5 text-xs'
      } ${
        selectedChip
          ? 'border-primary-600 bg-white text-primary-700 shadow-sm'
          : 'border-transparent bg-white/60 text-gray-500 hover:bg-white'
      }`}
    >
      {label}
      {count != null && (
        <span className={`min-w-[1.1rem] text-center rounded-full ${
          compact ? 'px-1 py-px text-[9px]' : 'px-1.5 py-0.5 text-[10px]'
        } ${
          selectedChip ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'
        }`}>
          {count}
        </span>
      )}
      {extra}
    </button>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-sky-50 via-white to-violet-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-sky-500 text-white flex items-center justify-center shadow-md shadow-sky-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Events</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Manage your events, polls and invited events. Invitations are waiting for your response.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openNewWizard}
              className="px-3.5 py-2 text-xs font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
            >
              + New Event
            </button>
            <button
              type="button"
              onClick={load}
              className="px-3.5 py-2 text-xs font-semibold rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 text-xs font-semibold rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              aria-label="Close events"
            >
              × Close
            </button>
          </div>
          {(shareTotals.owed > 0.004 || shareTotals.owe > 0.004) && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {shareTotals.owed > 0.004 && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">You are owed</p>
                  <p className="text-sm font-black tabular-nums text-emerald-600 leading-tight">
                    {formatShareMoney(shareTotals.owed, shareTotals.currency)}
                  </p>
                </div>
              )}
              {shareTotals.owe > 0.004 && (
                <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-rose-700">You owe</p>
                  <p className="text-sm font-black tabular-nums text-rose-600 leading-tight">
                    {formatShareMoney(shareTotals.owe, shareTotals.currency)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-6 px-5 border-b border-gray-200/80">
        {[
          { id: 'mine', label: 'My Events', count: mine.active.length + mine.completed.length },
          { id: 'drafts', label: 'Drafts', count: events.filter(isDraftEvent).length },
          { id: 'invited', label: 'Invited Events', count: invited.active.length + invited.completed.length },
          { id: 'hidden', label: "Don't show me", count: events.filter((event) => event?.viewerHidden && !isDraftEvent(event) && !isDeletedEvent(event)).length },
          { id: 'deleted', label: 'Deleted', count: events.filter(isDeletedEvent).length },
        ].map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setTab(item.id);
                if (item.id === 'hidden') setDateFilter('all');
              }}
              className={`pb-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-400 hover:text-gray-700'
              }`}
            >
              {item.label}
              <span className={`ml-1.5 text-[11px] font-bold ${active ? 'text-blue-500' : 'text-gray-400'}`}>{item.count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 pt-3">
        {[
          { id: 'all', label: 'My Event' },
          { id: 'invitation', label: 'Invitation' },
          { id: 'attendance', label: 'Attendance' },
          { id: 'expense', label: 'Expense' },
          { id: 'poll', label: 'Poll' },
          { id: 'tracking', label: 'Action tracking' },
          { id: 'meeting', label: 'Meeting' },
          { id: 'task', label: 'Task' },
        ].map((item) => filterChip(typeFilter === item.id, () => setTypeFilter(item.id), item.label))}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 pt-3">
        {tab !== 'hidden' && tab !== 'drafts' && tab !== 'deleted' && (
          <>
            {filterChip(mineStatus === 'active' && dateFilter === 'all', () => { setMineStatus('active'); setDateFilter('all'); }, 'Active', statusCounts.active, '', true)}
            {filterChip(mineStatus === 'completed', () => { setMineStatus('completed'); setDateFilter('all'); }, 'Completed', statusCounts.completed, '', true)}
            {filterChip(dateFilter === 'upcoming' && mineStatus === 'active', () => { setMineStatus('active'); setDateFilter('upcoming'); }, 'Upcoming', undefined, '', true)}
            {filterChip(dateFilter === 'today' && mineStatus === 'active', () => { setMineStatus('active'); setDateFilter('today'); }, 'Today', undefined, '', true)}
            {filterChip(dateFilter === 'week' && mineStatus === 'active', () => { setMineStatus('active'); setDateFilter('week'); }, 'This Week', undefined, '', true)}
            {filterChip(dateFilter === 'month' && mineStatus === 'active', () => { setMineStatus('active'); setDateFilter('month'); }, 'This Month', undefined, '', true)}
          </>
        )}
        <div className="ml-auto relative min-w-[12rem] flex-1 max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events..."
            className="w-full rounded-full border border-gray-200 bg-white pl-9 pr-3 py-1.5 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading events…</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {!loading && !error && tab === 'mine' && (
          <EventList
            events={mineList}
            currentUserId={currentUserId}
            directoryUsers={directoryUsers}
            onOpen={setSelected}
            onOpenOrganizer={setProfileUserId}
            onToggleHidden={handleToggleHidden}
            onEdit={mineStatus === 'active' ? (event) => {
              if (canEditEvent(event)) openEditWizard(event);
            } : undefined}
            onDelete={setPendingDelete}
            onClone={handleClone}
            emptyLabel={mineStatus === 'completed' ? 'No completed events' : 'No active events'}
          />
        )}
        {!loading && !error && tab === 'invited' && (
          <EventList
            events={mineStatus === 'completed' ? invitedCompleted : invitedActive}
            currentUserId={currentUserId}
            directoryUsers={directoryUsers}
            onOpen={setSelected}
            onOpenOrganizer={setProfileUserId}
            onToggleHidden={handleToggleHidden}
            onDelete={setPendingDelete}
            onClone={handleClone}
            emptyLabel={mineStatus === 'completed' ? 'No completed invited events' : 'No active invited events'}
          />
        )}
        {!loading && !error && tab === 'drafts' && (
          <EventList
            events={draftsList}
            currentUserId={currentUserId}
            directoryUsers={directoryUsers}
            onOpen={(event) => {
              if (canEditEvent(event)) openEditWizard(event);
              else setSelected(event);
            }}
            onOpenOrganizer={setProfileUserId}
            onEdit={(event) => {
              if (canEditEvent(event)) openEditWizard(event);
            }}
            onDelete={setPendingDelete}
            onClone={handleClone}
            emptyLabel="No draft events"
          />
        )}
        {!loading && !error && tab === 'deleted' && (
          <EventList
            events={deletedList}
            currentUserId={currentUserId}
            directoryUsers={directoryUsers}
            onOpen={setSelected}
            onOpenOrganizer={setProfileUserId}
            onClone={handleClone}
            emptyLabel="No deleted events"
          />
        )}
        {!loading && !error && tab === 'hidden' && (
          <EventList
            events={hiddenList}
            currentUserId={currentUserId}
            directoryUsers={directoryUsers}
            onOpen={setSelected}
            onOpenOrganizer={setProfileUserId}
            onToggleHidden={handleToggleHidden}
            onEdit={(event) => {
              if (canEditEvent(event)) openEditWizard(event);
            }}
            onDelete={setPendingDelete}
            onClone={handleClone}
            hiddenMode
            emptyLabel="No hidden events. Hide one with the eye icon and it will appear here."
          />
        )}
      </div>

      <EventDetailModal
        isOpen={Boolean(selectedInfo)}
        onClose={() => {
          setSelected(null);
          setRespondError('');
          load();
        }}
        eventInfo={selectedInfo}
        currentUserId={currentUserId}
        saving={saving}
        error={respondError}
        onEditEvent={selected?.viewerIsCreator ? () => openEditWizard(selected) : undefined}
        onCloneEvent={() => handleClone(selected)}
        onCloseEvent={selected?.viewerIsCreator ? handleCloseEvent : undefined}
        onReopenEvent={selected?.viewerIsCreator ? handleReopenEvent : undefined}
        onPollVote={(selectedOptions) => handleRespond(selectedOptions.length ? 'VOTE' : 'UNVOTE', { selectedOptions })}
        onRespond={handleRespondRequest}
        responseOptions={String(responseType).toUpperCase() === 'POLL' ? [] : getResponseOptions(responseType)}
        onEditRsvp={canRespond && myStatus === 'ACCEPTED' && (hasEnabledRsvpFields(selectedInfo?.rsvpFields) || myParticipant?.responseDetails) ? handleEditRsvp : undefined}
        canRespond={canRespond}
        myStatus={myStatus}
        hasDecided={hasDecided}
        myRsvpSummary={formatRsvpSummary(myParticipant?.responseDetails)}
      />

      <RsvpDetailsModal
        isOpen={showRsvpModal}
        onClose={() => {
          if (!saving) {
            setShowRsvpModal(false);
            setPendingResponse(null);
            setRsvpFieldsForModal(null);
            setEditingRsvp(false);
          }
        }}
        onSubmit={(details) => handleRespond(pendingResponse || 'ACCEPTED', details)}
        eventId={selected?.id}
        userId={currentUserId}
        eventName={selected?.name}
        rsvpFields={rsvpFieldsForModal || selectedInfo?.rsvpFields}
        saving={saving}
        initialDetails={myParticipant?.responseDetails}
        isEditing={editingRsvp}
      />

      <FollowUpEventPrompt
        isOpen={Boolean(followUpSource) && !showWizard}
        title="Poll closed"
        description="Do you want to create another event, like expense sharing, a poll, or a calculation?"
        includePoll
        askParticipants
        onClose={() => setFollowUpSource(null)}
        onContinue={({ eventType, participantMode }) => {
          const people = followUpParticipantsFromPoll(followUpSource, participantMode, currentUserId);
          setFollowUpChannelId(followUpSource?.channelId || null);
          setFollowUpPollName(pollDisplayName(followUpSource));
          setFollowUpType(eventType);
          setFollowUpPeople(people);
          setFollowUpSource(null);
          setEditingEvent(null);
          setShowWizard(true);
        }}
      />

      <CreateEventWizard
        isOpen={showWizard}
        personalMode={!editingEvent && !followUpChannelId}
        channel={followUpChannelId
          ? { id: followUpChannelId }
          : (editingEvent?.channelId ? { id: editingEvent.channelId } : undefined)}
        editingEvent={editingEvent}
        initialEventType={followUpType}
        initialParticipants={followUpPeople}
        sourceEventName={followUpPollName}
        skipPostedFollowUp={Boolean(followUpPeople)}
        onClose={() => {
          setShowWizard(false);
          setEditingEvent(null);
          setFollowUpType(null);
          setFollowUpPeople(null);
          setFollowUpChannelId(null);
          setFollowUpPollName('');
        }}
        onCreated={() => {
          setShowWizard(false);
          setEditingEvent(null);
          setFollowUpType(null);
          setFollowUpPeople(null);
          setFollowUpChannelId(null);
          setFollowUpPollName('');
          load();
        }}
      />

      <UserProfileModal
        userId={profileUserId}
        isOpen={Boolean(profileUserId)}
        onClose={() => setProfileUserId(null)}
      />

      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete event"
        message={`Move "${pendingDelete?.name || 'this event'}" to Deleted events?`}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

const ACTIVE_VIEWER_STATUSES = new Set(['ACCEPTED']);

const isDeletedEvent = (event) => {
  const status = String(event?.status || '').toUpperCase();
  return status === 'DELETED' || status === 'CANCELLED';
};

const isDraftEvent = (event) => {
  if (isDeletedEvent(event)) return false;
  const status = String(event?.status || '').toUpperCase();
  if (status === 'DRAFT') return true;
  return event?.draft === true;
};

const matchesTypeFilter = (event, type) => {
  const eventType = String(event?.eventType || '').toUpperCase();
  if (type === 'invitation') return eventType === 'ATTENDANCE';
  if (type === 'attendance') return eventType === 'DAILY_ATTENDANCE';
  if (type === 'expense') return eventType === 'SPLITWISE' || eventType === 'EXPENSE_TRACKING';
  if (type === 'poll') return eventType === 'POLL';
  if (type === 'tracking') return eventType === 'ACTION_TRACKING';
  if (type === 'meeting') return eventType === 'MEETING';
  if (type === 'task') return eventType === 'TASK';
  return true;
};

const isActiveForUser = (event) => {
  if (isDraftEvent(event) || isDeletedEvent(event)) return false;
  const eventType = String(event?.eventType || '').toUpperCase();
  if (eventType === 'POLL' || eventType === 'ACTION_TRACKING') {
    return String(event?.viewerStatus || '').toUpperCase() !== 'DECLINED';
  }
  if (event?.viewerIsCreator) return true;
  return ACTIVE_VIEWER_STATUSES.has(String(event?.viewerStatus || '').toUpperCase());
};

const isDeclinedForUser = (event) => String(event?.viewerStatus || '').toUpperCase() === 'DECLINED';

const myStatusFrom = (dto, userId) => {
  const match = (dto.participants || []).find((p) => sameId(p.userId, userId));
  return match?.status || null;
};

export default EventsHomePanel;
